// Scanner PDF (0.49): páginas, pastas, PDF, onde guardar e IA.
// As fotos das páginas ficam só neste aparelho (não ocupam o servidor). O PDF pronto vai para onde a pessoa
// escolher: o próprio aparelho, o Google Drive ou o OneDrive dela. O PDF é montado no próprio aparelho.
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { LineCapStyle, PDFDocument, StandardFonts, rgb, type PDFPage } from 'pdf-lib';
import { getDoc, setDoc } from '../store/store';
import type { ScanDoc, ScanPagina, FiltroScan, Traco, DestinoScan } from '../store/types';
import { uid, br, hm } from './date';
import { t } from './i18n';
import { paraB64 } from './cofre';
import { limparTexto } from './pdf';
import { perguntar, idiomaResposta } from './ia';
import { salvarECompartilhar } from './arquivoSalvar';
import { contaDe } from './nuvem';
import { guardarBytes, nomeDestino } from './guardar';
import { semBloqueio } from '../auth/AuthContext';
import { codificar, decodificar, detectarCantos, ehJpeg, ehPng, jpegMenor, tratar, type Cantos } from './scanImagem';
import { aparelhoScan, apagarArquivo, apagarPastaDoc, existeLocal, gravar, lerBytes, tamanhoDe, temTratamento, escolherPasta, pastaAtual } from './scanArquivos';

const MAX_LADO = 2000;

// ─── documentos e pastas ───
const mudarDocs = (fn: (docs: ScanDoc[]) => ScanDoc[]) => setDoc('scanner', d => ({ ...d, docs: fn(d.docs ?? []) }));
export const docScan = (id: string) => (getDoc('scanner').docs ?? []).find(d => d.id === id);
export function mudarDoc(id: string, p: Partial<ScanDoc>) { mudarDocs(ds => ds.map(d => (d.id === id ? { ...d, ...p } : d))); }
function mudarPagina(doc: string, pag: string, p: Partial<ScanPagina>) {
  mudarDocs(ds => ds.map(d => (d.id === doc ? { ...d, paginas: d.paginas.map(x => (x.id === pag ? { ...x, ...p } : x)) } : d)));
}
export const nomePadrao = (tipo: 'doc' | 'id') => {
  const agora = new Date();
  return `${tipo === 'id' ? t('Identidade') : t('Documento')} ${br(agora)} ${hm(agora)}`;
};
export function criarDoc(tipo: 'doc' | 'id', pasta: string | null, paginas: ScanPagina[], id = uid()): ScanDoc {
  const d: ScanDoc = { id, nome: nomePadrao(tipo), pasta, criado: new Date().toISOString(), tipo, paginas, tamanho: 'a4' };
  mudarDocs(ds => [d, ...ds]);
  return d;
}
export async function apagarDoc(id: string) {
  const d = docScan(id);
  mudarDocs(ds => ds.filter(x => x.id !== id));
  if (d) apagarPastaDoc(id);
}
export async function apagarPagina(doc: string, pag: string) {
  const d = docScan(doc); const p = d?.paginas.find(x => x.id === pag);
  if (!d || !p) return;
  mudarDocs(ds => ds.map(x => (x.id === doc ? { ...x, paginas: x.paginas.filter(y => y.id !== pag) } : x)));
  if (await ehDaqui(p)) { apagarArquivo(p.local?.bruta); apagarArquivo(p.local?.final); }
}
export const ordenarPaginas = (doc: string, paginas: ScanPagina[]) => mudarDoc(doc, { paginas });

export function novaPasta(nome: string) {
  const p = { id: uid(), nome: nome.trim() };
  setDoc('scanner', d => ({ ...d, pastas: [...(d.pastas ?? []), p] }));
  return p;
}
export const renomearPasta = (id: string, nome: string) => setDoc('scanner', d => ({ ...d, pastas: d.pastas.map(p => (p.id === id ? { ...p, nome: nome.trim() } : p)) }));
/** Apaga a pasta; os documentos dela continuam, em "Todos". */
export const apagarPasta = (id: string) => setDoc('scanner', d => ({ ...d, pastas: d.pastas.filter(p => p.id !== id), docs: d.docs.map(x => (x.pasta === id ? { ...x, pasta: null } : x)) }));

// ─── imagens ───
async function ehDaqui(p: ScanPagina) { return !!p.local && p.local.aparelho === (await aparelhoScan()); }

/** Endereço da foto original (para ajustar os cantos). */
export async function fonteBruta(doc: string, p: ScanPagina): Promise<string | null> {
  if ((await ehDaqui(p)) && existeLocal(p.local?.bruta)) return p.local!.bruta!;
  return fontePagina(doc, p);
}
export const adicionarPaginas = (doc: string, paginas: ScanPagina[]) => mudarDocs(ds => ds.map(d => (d.id === doc ? { ...d, paginas: [...d.paginas, ...paginas] } : d)));

/** Endereço para mostrar a página (as fotos ficam no aparelho onde foram escaneadas). */
export async function fontePagina(_doc: string, p: ScanPagina): Promise<string | null> {
  if ((await ehDaqui(p)) && existeLocal(p.local?.final)) return p.local!.final!;
  return null;
}
/** As páginas deste documento estão neste aparelho? */
export async function estaAqui(d: ScanDoc) {
  const a = await aparelhoScan();
  return d.paginas.every(p => p.local?.aparelho === a && existeLocal(p.local.final));
}
async function bytesFinal(_doc: string, p: ScanPagina): Promise<Uint8Array> {
  if ((await ehDaqui(p)) && existeLocal(p.local?.final)) return lerBytes(p.local!.final!);
  throw new Error(t('As páginas deste documento estão no dispositivo onde ele foi escaneado. Abra por lá ou use a cópia guardada no Drive ou no OneDrive.'));
}
async function bytesBruta(doc: string, p: ScanPagina): Promise<Uint8Array> {
  if ((await ehDaqui(p)) && existeLocal(p.local?.bruta)) return lerBytes(p.local!.bruta!);
  return bytesFinal(doc, p);
}

/** Diminui a foto (o lado maior fica com até 2000 px) e devolve os bytes em JPEG. */
async function prepararFoto(uri: string): Promise<Uint8Array> {
  if (!temTratamento()) return lerBytes(uri);
  const IM = require('expo-image-manipulator');
  const { w, h } = await tamanhoDe(uri).catch(() => ({ w: 0, h: 0 }));
  const k = w && h ? Math.min(1, MAX_LADO / Math.max(w, h)) : 1;
  const r = await IM.manipulateAsync(uri, k < 1 ? [{ resize: { width: Math.round(w * k) } }] : [], { compress: 0.9, format: IM.SaveFormat.JPEG });
  return lerBytes(r.uri);
}

const respirar = () => new Promise(r => setTimeout(r, 30));   // deixa a tela mostrar o "aguarde" antes da conta pesada

/** Transforma fotos (do scanner, da câmera ou da galeria) em páginas. */
export type Foto = { uri: string; cantos?: Cantos | null };
/** Acha os cantos da folha numa foto (para já começar o ajuste no lugar certo). */
export async function cantosAutomaticos(uri: string): Promise<Cantos | null> {
  if (!temTratamento()) return null;
  try { const b = await prepararFoto(uri); return ehJpeg(b) ? detectarCantos(decodificar(b)) : null; } catch { return null; }
}

/** auto: acha a folha sozinho nas fotos sem cantos (fotos importadas da galeria). */
export async function paginasDeFotos(doc: string, fotos: Foto[], filtro: FiltroScan, aoAvancar?: (i: number) => void, auto = false): Promise<ScanPagina[]> {
  const aparelho = await aparelhoScan();
  const out: ScanPagina[] = [];
  for (let i = 0; i < fotos.length; i++) {
    aoAvancar?.(i);
    await respirar();
    const id = uid();
    const bruta = await prepararFoto(fotos[i].uri);
    const uBruta = await gravar(doc, `${id}-b.jpg`, bruta);
    let cantos = fotos[i].cantos ?? null;
    if (!cantos && auto && temTratamento() && ehJpeg(bruta)) { await respirar(); cantos = detectarCantos(decodificar(bruta)); }
    const p: ScanPagina = { id, v: 1, cantos, giro: 0, filtro, local: { aparelho, bruta: uBruta } };
    const final = await processar(bruta, p);
    p.local!.final = final.bytes === bruta ? uBruta : await gravar(doc, `${id}-1.jpg`, final.bytes);
    p.w = final.w; p.h = final.h;
    out.push(p);
  }
  return out;
}

async function processar(bruta: Uint8Array, p: Pick<ScanPagina, 'cantos' | 'giro' | 'filtro'>): Promise<{ bytes: Uint8Array; w: number; h: number }> {
  const semMudanca = !p.cantos && !p.giro && (p.filtro ?? 'original') === 'original';
  // sem o módulo de imagem (APK antigo) a foto vem em tamanho cheio: pesada demais para tratar aqui
  if (semMudanca || !ehJpeg(bruta) || !temTratamento()) return { bytes: bruta, w: 0, h: 0 };
  await respirar();
  const img = tratar(decodificar(bruta), { cantos: p.cantos as Cantos | null, giro: p.giro, filtro: p.filtro }, MAX_LADO);
  return { bytes: codificar(img, 85), w: img.w, h: img.h };
}

/** Muda o recorte, o giro ou o filtro de uma página e refaz a imagem tratada. */
export async function retratar(doc: string, pag: string, mudanca: Partial<Pick<ScanPagina, 'cantos' | 'giro' | 'filtro'>>) {
  const d = docScan(doc); const p = d?.paginas.find(x => x.id === pag);
  if (!d || !p) return;
  const novo = { ...p, ...mudanca };
  const bruta = await bytesBruta(doc, p);
  const final = await processar(bruta, novo);
  const aparelho = await aparelhoScan();
  const v = p.v + 1;
  const daqui = await ehDaqui(p);
  const uBruta = daqui && existeLocal(p.local?.bruta) ? p.local!.bruta! : await gravar(doc, `${p.id}-b.jpg`, bruta);
  const uFinal = await gravar(doc, `${p.id}-${v}.jpg`, final.bytes);
  if (daqui && p.local?.final && p.local.final !== p.local.bruta) apagarArquivo(p.local.final);
  mudarPagina(doc, pag, { cantos: novo.cantos ?? null, giro: novo.giro ?? 0, filtro: novo.filtro, v, w: final.w || p.w, h: final.h || p.h,
    local: { aparelho, bruta: uBruta, final: uFinal } });
}

// ─── PDF ───
const A4: [number, number] = [595.28, 841.89];
const CARTA: [number, number] = [612, 792];

function caixaDosTracos(tracos: Traco[]) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const tr of tracos) for (const m of tr.d.matchAll(/[ML](-?[\d.]+) (-?[\d.]+)/g)) {
    const x = +m[1], y = +m[2];
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return isFinite(x0) ? { x0, y0, x1: Math.max(x1, x0 + 1), y1: Math.max(y1, y0 + 1) } : null;
}

export async function montarPdfScan(d: ScanDoc, aoAvancar?: (i: number) => void): Promise<Uint8Array> {
  if (!d.paginas.length) throw new Error(t('O documento não tem páginas.'));
  const pdf = await PDFDocument.create();
  pdf.setTitle(d.nome); pdf.setCreator('My Planner Anne'); pdf.setProducer('My Planner Anne');
  const fonte = await pdf.embedFont(StandardFonts.Helvetica);
  const imgs = [];
  for (let i = 0; i < d.paginas.length; i++) {
    aoAvancar?.(i);
    await respirar();
    let b = await bytesFinal(d.id, d.paginas[i]);
    if (d.menor && ehJpeg(b)) b = jpegMenor(b, 1400, 60);
    imgs.push(ehPng(b) ? await pdf.embedPng(b) : await pdf.embedJpg(b));
  }
  const paginasPdf: PDFPage[] = [];
  if (d.tipo === 'id') {
    // documento de identidade: frente e verso na mesma folha A4, no tamanho real do cartão (85,6 mm)
    const larg = 242.6;
    for (let i = 0; i < imgs.length; i += 2) {
      const pg = pdf.addPage(A4); paginasPdf.push(pg);
      let y = A4[1] - 90;
      for (const im of imgs.slice(i, i + 2)) {
        const alt = (im.height / im.width) * larg;
        y -= alt;
        pg.drawImage(im, { x: (A4[0] - larg) / 2, y, width: larg, height: alt });
        y -= 36;
      }
    }
  } else {
    imgs.forEach(im => {
      const deitada = im.width > im.height;
      let tam: [number, number] = d.tamanho === 'carta' ? CARTA : A4;
      if (d.tamanho === 'foto') tam = deitada ? [A4[1], (A4[1] * im.height) / im.width] : [A4[0], (A4[0] * im.height) / im.width];
      else if (deitada) tam = [tam[1], tam[0]];
      const pg = pdf.addPage(tam); paginasPdf.push(pg);
      const k = Math.min(tam[0] / im.width, tam[1] / im.height);
      const w = im.width * k, h = im.height * k;
      pg.drawImage(im, { x: (tam[0] - w) / 2, y: (tam[1] - h) / 2, width: w, height: h });
    });
  }
  // texto lido pela IA, invisível por baixo da imagem: dá para buscar e copiar no leitor de PDF
  d.paginas.forEach((p, i) => {
    const pg = paginasPdf[d.tipo === 'id' ? Math.floor(i / 2) : i];
    if (!p.texto || !pg) return;
    let y = pg.getHeight() - 20;
    for (const linha of limparTexto(p.texto).split('\n')) {
      if (y < 10) break;
      try { pg.drawText(linha.slice(0, 160), { x: 12, y, size: 7, font: fonte, opacity: 0 }); } catch { /* caractere fora da fonte */ }
      y -= 9;
    }
  });
  // assinatura desenhada no app
  const a = d.assinatura;
  const caixa = a ? caixaDosTracos(a.tracos) : null;
  const pg = a ? paginasPdf[Math.min(paginasPdf.length - 1, Math.max(0, a.pagina))] : null;
  if (a && caixa && pg) {
    const W = pg.getWidth();
    let k = (W * 0.3) / (caixa.x1 - caixa.x0);
    k = Math.min(k, 70 / (caixa.y1 - caixa.y0));
    const lw = (caixa.x1 - caixa.x0) * k, lh = (caixa.y1 - caixa.y0) * k;
    const x = a.lugar === 'esq' ? 40 : a.lugar === 'centro' ? (W - lw) / 2 : W - 40 - lw;
    const yTopo = 36 + lh;
    for (const tr of a.tracos) {
      pg.drawSvgPath(tr.d, { x: x - caixa.x0 * k, y: yTopo + caixa.y0 * k, scale: k, borderColor: rgb(0.06, 0.12, 0.45), borderWidth: Math.max(0.8, tr.w * k), borderLineCap: LineCapStyle.Round });
    }
  }
  return pdf.save();
}

const nomeArquivo = (d: ScanDoc) => (d.nome.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'documento') + '.pdf';

export async function compartilharPdf(d: ScanDoc, aoAvancar?: (i: number) => void) {
  const bytes = await montarPdfScan(d, aoAvancar);
  await salvarECompartilhar(bytes, nomeArquivo(d), 'application/pdf');
  return bytes.length;
}

// ─── onde guardar o PDF: no aparelho, no Google Drive ou no OneDrive ───
export const destinoScan = (): DestinoScan => getDoc('scanner').guardar ?? 'aparelho';
export const mudarDestino = (g: DestinoScan) => setDoc('scanner', d => ({ ...d, guardar: g }));
/** Marca do conteúdo: muda quando o documento muda (para saber se a cópia guardada ficou velha). */
export const marcaDoc = (d: ScanDoc) => JSON.stringify([d.nome, d.tipo, d.tamanho ?? 'a4', !!d.menor, d.assinatura ? [d.assinatura.pagina, d.assinatura.lugar, d.assinatura.tracos.length] : 0,
  d.paginas.map(p => `${p.id}:${p.v}:${p.texto != null ? 1 : 0}`)]);
export const copiaEmDia = (d: ScanDoc) => !!d.salvo && d.salvo.sig === marcaDoc(d);
export { nomeDestino };

/** Guarda o PDF no destino. semPerguntar: não abre janelas (usado ao sair do documento). Devolve a mensagem para mostrar. */
export async function guardarPdf(d: ScanDoc, onde: DestinoScan = destinoScan(), aoAvancar?: (i: number) => void, semPerguntar = false): Promise<string | null> {
  if (semPerguntar && onde !== 'aparelho' && !contaDe(onde)) return null;
  if (semPerguntar && onde === 'aparelho' && Platform.OS !== 'android') return null;
  const sig = marcaDoc(d);
  const bytes = await montarPdfScan(d, aoAvancar);
  const r = await guardarBytes(nomeArquivo(d), 'application/pdf', bytes, onde, semPerguntar);
  if (!r) return null;
  const { msg, ...salvo } = r;
  mudarDoc(d.id, { salvo: { ...salvo, sig } });
  return msg;
}
export { escolherPasta, pastaAtual };

// ─── IA (a do próprio usuário, com a chave dele) ───
async function imagemParaIA(doc: string, p: ScanPagina) {
  let b = await bytesFinal(doc, p);
  if (ehJpeg(b)) b = jpegMenor(b, 1600, 75);
  return { mime: ehPng(b) ? 'image/png' : 'image/jpeg', base64: paraB64(b) };
}

/** Lê o texto de cada página (as que ainda não foram lidas). */
export async function lerTexto(docId: string, aoAvancar?: (i: number, n: number) => void) {
  const d = docScan(docId); if (!d) return '';
  const sistema = 'Você lê páginas escaneadas. Transcreva todo o texto da imagem na ordem de leitura, com as quebras de linha, os números e a pontuação exatamente como aparecem. Não resuma, não traduza e não comente. Se não houver texto, responda com uma linha vazia.';
  for (let i = 0; i < d.paginas.length; i++) {
    const p = d.paginas[i];
    if (p.texto != null) continue;
    aoAvancar?.(i, d.paginas.length);
    const texto = (await perguntar(sistema, 'Transcreva esta página.', [await imagemParaIA(d.id, p)])).trim();
    mudarPagina(d.id, p.id, { texto });
  }
  return textoDoDoc(docScan(docId)!);
}
export const textoDoDoc = (d: ScanDoc) => d.paginas.map(p => p.texto ?? '').filter(Boolean).join('\n\n');

export async function resumir(docId: string, aoAvancar?: (i: number, n: number) => void) {
  const texto = await lerTexto(docId, aoAvancar);
  if (!texto.trim()) throw new Error(t('Não encontrei texto neste documento.'));
  const r = (await perguntar(`Resuma o documento enviado em ${idiomaResposta()}, em até 8 linhas curtas: do que se trata, quem são as partes, valores, datas e prazos importantes. Sem markdown.`, texto)).trim();
  mudarDoc(docId, { resumo: r });
  return r;
}

export async function perguntarAoDoc(docId: string, pergunta: string, aoAvancar?: (i: number, n: number) => void) {
  const texto = await lerTexto(docId, aoAvancar);
  return (await perguntar(`Responda em ${idiomaResposta()}, de forma curta e direta, só com base no documento abaixo. Se a resposta não estiver no documento, diga isso.\n\nDOCUMENTO:\n${texto.slice(0, 60000)}`, pergunta)).trim();
}
